import React, { useEffect, useState } from 'react';
import { Check, ChevronsUpDown, Loader2, Search, User, Users } from 'lucide-react';
import { cn, formatToLocalDate, toTitleCase } from '@/lib/utils';
import { formatDOB } from '@/lib/dateUtils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useClinic } from '@/contexts/ClinicContext';
import { taskService, TaskSearchPatient } from '@/services/taskService';

interface TaskPatientSelectProps {
  onValueChange: (value: TaskSearchPatient) => void;
  className?: string;
  placeholder?: string;
  hasError?: boolean;
}

const TaskPatientSelect: React.FC<TaskPatientSelectProps> = ({
  onValueChange,
  className,
  placeholder = 'Search patients...',
  hasError = false,
}) => {
  const { clinic } = useClinic();
  const [open, setOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<TaskSearchPatient | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [patients, setPatients] = useState<TaskSearchPatient[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    const debounceTimer = setTimeout(async () => {
      setIsLoading(true);
      try {
        const response = await taskService.searchPatients(searchValue.trim(), clinic?.id);
        setPatients(response?.data?.patients || []);
      } catch (error) {
        console.error('Failed to search task patients:', error);
        setPatients([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchValue, clinic?.id, open]);

  const handleSelection = (patientId: string) => {
    const patient = patients.find((item) => item.id === patientId);
    if (!patient) {
      return;
    }

    setSelectedPatient(patient);
    onValueChange(patient);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between h-10 border-slate-200 bg-white hover:bg-slate-50',
            hasError && 'border-red-500',
            className
          )}
        >
          {selectedPatient ? (
            <div className="flex items-center gap-2 truncate">
              <div className="w-6 h-6 bg-brand-teal/10 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="h-3 w-3 text-brand-teal" />
              </div>
              <span className="truncate text-slate-900 font-medium text-sm">
                {toTitleCase(selectedPatient.full_name || '')}
              </span>
            </div>
          ) : (
            <span className="text-slate-500 text-sm">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-slate-400" />
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-[440px] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-teal/10 flex items-center justify-center flex-shrink-0">
              <Users className="h-5 w-5 text-brand-teal" />
            </div>
            <div>
              <DialogTitle className="text-base font-semibold text-slate-900">
                Select Patient
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500 mt-0.5">
                Search and select a patient for this task
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name or patient ID..."
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              className="pl-9 h-10 bg-white border-slate-200 text-sm placeholder:text-slate-400"
            />
            {isLoading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 className="h-4 w-4 text-brand-teal animate-spin" />
              </div>
            )}
          </div>
        </div>

        <ScrollArea className="max-h-[320px]">
          {isLoading && patients.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <Loader2 className="h-8 w-8 text-brand-teal animate-spin mb-3" />
              <p className="text-sm text-slate-500">Searching patients...</p>
            </div>
          ) : patients.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 px-4">
              <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                <User className="h-7 w-7 text-slate-300" />
              </div>
              <p className="text-sm font-medium text-slate-700">No patients found</p>
            </div>
          ) : (
            <div className="py-1">
              {patients.map((patient) => {
                const isSelected = selectedPatient?.id === patient.id;

                return (
                  <button
                    key={patient.id}
                    type="button"
                    onClick={() => handleSelection(patient.id)}
                    className={cn(
                      'w-full flex items-center gap-3 px-5 py-3 text-left transition-colors',
                      isSelected
                        ? 'bg-brand-teal/5 border-l-2 border-brand-teal'
                        : 'hover:bg-slate-50 border-l-2 border-transparent'
                    )}
                  >
                    <div
                      className={cn(
                        'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0',
                        isSelected ? 'bg-brand-teal/10' : 'bg-slate-100'
                      )}
                    >
                      <User
                        className={cn(
                          'h-4 w-4',
                          isSelected ? 'text-brand-teal' : 'text-slate-500'
                        )}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            'font-medium text-sm truncate',
                            isSelected ? 'text-brand-teal' : 'text-slate-900'
                          )}
                        >
                          {toTitleCase(patient.full_name || '')}
                        </span>
                        {isSelected && (
                          <Check className="h-4 w-4 text-brand-teal flex-shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        {patient.dob && (
                          <span className="text-[11px] text-slate-400">
                            DOB: {formatDOB(patient.dob)}
                          </span>
                        )}
                        {patient.gender && (
                          <Badge
                            variant="secondary"
                            className="text-[10px] px-1.5 py-0 h-4 bg-slate-100 hover:bg-slate-100 text-slate-600 font-medium"
                          >
                            {toTitleCase(patient.gender)}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default TaskPatientSelect;
